import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FileText,
  ListChecks,
  ClipboardList,
  Sparkles,
  Loader2,
  Copy,
  Download,
  FileUp,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function MeetingNotesDialog({ meetingId, onClose, onChanged }) {
  const [m, setM] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [checked, setChecked] = useState({});
  const [showTranscript, setShowTranscript] = useState(false);

  const load = useCallback(() => {
    if (!meetingId) return;
    api.get(`/meetings/${meetingId}`).then((r) => setM(r.data)).catch(() => {});
  }, [meetingId]);

  useEffect(() => { setM(null); setChecked({}); setShowTranscript(false); load(); }, [meetingId, load]);

  useEffect(() => {
    if (m?.analysis_status !== "processing") return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [m?.analysis_status, load]);

  const analysis = m?.analysis;
  const notesText = () => {
    const a = analysis || {};
    let t = `${m.title}\nClient: ${m.client_name || ""}\nDate: ${m.meeting_date}\n\nEXECUTIVE SUMMARY\n${a.executive_summary || ""}\n\nKEY TAKEAWAYS\n`;
    (a.key_takeaways || []).forEach((k) => (t += `- ${k}\n`));
    t += `\nACTION ITEMS / TO-DO\n`;
    (a.action_items || []).forEach((it) => (t += `[ ] ${it.task} — ${it.assignee || "Unassigned"}\n`));
    return t;
  };

  const copyNotes = async () => {
    await navigator.clipboard.writeText(notesText());
    toast.success("Notes copied to clipboard");
  };

  const downloadNotes = () => {
    const blob = new Blob([notesText()], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${m.title} - AI Notes.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToDoc = async () => {
    setExporting(true);
    try {
      const res = await api.post(`/meetings/${meetingId}/export-doc`);
      toast.success("Exported to the client's Google Drive folder");
      const blob = new Blob([res.data.content], { type: "application/msword" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${res.data.name.replace(".gdoc", "")}.doc`;
      a.click();
      URL.revokeObjectURL(url);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const reAnalyze = async () => {
    try {
      await api.post(`/meetings/${meetingId}/analyze`);
      setM((prev) => ({ ...prev, analysis_status: "processing" }));
      toast.info("Analyzing transcript…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze");
    }
  };

  return (
    <Dialog open={!!meetingId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl tracking-tight">{m?.title}</DialogTitle>
          <DialogDescription className="font-[Manrope]">AI Meeting Notes · {fmtDate(m?.meeting_date)}</DialogDescription>
        </DialogHeader>

        {!m ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#193962]" /></div>
        ) : m.analysis_status === "processing" ? (
          <div className="py-16 flex flex-col items-center gap-3" data-testid="analysis-processing">
            <div className="flex items-center gap-2 text-[#193962]"><Sparkles className="h-5 w-5" /><Loader2 className="h-5 w-5 animate-spin" /></div>
            <p className="font-[Manrope] text-sm text-muted-foreground">AI is analyzing the transcript…</p>
          </div>
        ) : m.analysis_status === "failed" || !analysis ? (
          <div className="py-12 flex flex-col items-center gap-4" data-testid="analysis-empty">
            <p className="font-[Manrope] text-sm text-muted-foreground">No AI notes yet for this meeting.</p>
            <Button onClick={reAnalyze} data-testid="analyze-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
              <Sparkles className="h-4 w-4 mr-2" /> Analyze with AI
            </Button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="ai-notes">
            {/* Action bar */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={exportToDoc} disabled={exporting} data-testid="export-doc-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
                <FileUp className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting…" : "Export to Google Doc"}
              </Button>
              <Button size="sm" variant="outline" onClick={copyNotes} data-testid="copy-notes-btn"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
              <Button size="sm" variant="outline" onClick={downloadNotes} data-testid="download-notes-btn"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
              <Button size="sm" variant="ghost" onClick={reAnalyze} data-testid="reanalyze-btn" className="text-muted-foreground"><RefreshCw className="h-4 w-4 mr-1.5" /> Re-analyze</Button>
            </div>

            {/* Executive summary */}
            <section data-testid="section-summary">
              <div className="flex items-center gap-2 mb-2"><FileText className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Executive Summary</h3></div>
              <p className="font-[Manrope] text-sm leading-relaxed text-zinc-700">{analysis.executive_summary || "—"}</p>
            </section>

            {/* Key takeaways */}
            <section data-testid="section-takeaways">
              <div className="flex items-center gap-2 mb-2"><ListChecks className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Key Takeaways</h3></div>
              <ul className="space-y-1.5">
                {(analysis.key_takeaways || []).map((k, i) => (
                  <li key={i} className="flex gap-2.5 font-[Manrope] text-sm text-zinc-700">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#193962] shrink-0" />{k}
                  </li>
                ))}
                {(!analysis.key_takeaways || analysis.key_takeaways.length === 0) && <li className="text-sm text-muted-foreground font-[Manrope]">—</li>}
              </ul>
            </section>

            {/* Action items */}
            <section data-testid="section-actions">
              <div className="flex items-center gap-2 mb-2"><ClipboardList className="h-4 w-4 text-[#193962]" /><h3 className="font-[Outfit] font-semibold tracking-tight">Action Items / To-Do</h3></div>
              <div className="space-y-2">
                {(analysis.action_items || []).map((it, i) => (
                  <label key={i} className="flex items-start gap-3 border border-border rounded-md px-3 py-2.5 cursor-pointer hover:bg-zinc-50 transition-colors" data-testid={`action-item-${i}`}>
                    <Checkbox checked={!!checked[i]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: v }))} className="mt-0.5" />
                    <div className="min-w-0">
                      <p className={`font-[Manrope] text-sm ${checked[i] ? "line-through text-zinc-400" : "text-zinc-800"}`}>{it.task}</p>
                      <p className="text-xs text-[#193962] font-[Manrope]">{it.assignee || "Unassigned"}</p>
                    </div>
                  </label>
                ))}
                {(!analysis.action_items || analysis.action_items.length === 0) && <p className="text-sm text-muted-foreground font-[Manrope]">No action items detected.</p>}
              </div>
            </section>

            {/* Transcript (collapsible) */}
            <section className="border-t border-border pt-4">
              <button onClick={() => setShowTranscript((s) => !s)} data-testid="toggle-transcript" className="flex items-center gap-2 text-sm font-[Manrope] text-muted-foreground hover:text-zinc-900 transition-colors">
                <ChevronDown className={`h-4 w-4 transition-transform ${showTranscript ? "rotate-180" : ""}`} /> Full transcript
              </button>
              {showTranscript && (
                <div className="mt-3 border border-border rounded-md p-4 bg-zinc-50" data-testid="transcript-text">
                  <p className="font-[Manrope] text-sm whitespace-pre-wrap leading-relaxed text-zinc-700">{m.transcript || "—"}</p>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
